
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import ProfileTab from './tabs/ProfileTab';
import SubscriptionTab from './tabs/SubscriptionTab';
import ActivityLogTab from './tabs/ActivityLogTab';
import BrandingTab from './tabs/BrandingTab';

type Tab = 'profile' | 'branding' | 'subscription' | 'audit';

export default function Settings() {
    const [searchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab') as Tab | null;
    const [activeTab, setActiveTab] = useState<Tab>(tabFromUrl && ['profile', 'branding', 'subscription', 'audit'].includes(tabFromUrl) ? tabFromUrl : 'profile');

    const renderContent = () => {
        switch (activeTab) {
            case 'profile': return <ProfileTab />;
            case 'branding': return <BrandingTab />;
            case 'subscription': return <SubscriptionTab />;
            case 'audit': return <ActivityLogTab />;
            default: return <ProfileTab />;
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
            <Navbar />

            <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">Account Settings</h2>
                    <p className="mt-1 text-sm text-gray-500">Manage your profile, subscription, and view security logs.</p>
                </div>

                <div className="lg:grid lg:grid-cols-12 lg:gap-x-5">

                    {/* Sidebar Navigation */}
                    <aside className="py-6 px-2 sm:px-6 lg:py-0 lg:px-0 lg:col-span-3">
                        <nav className="space-y-1">

                            <button
                                onClick={() => setActiveTab('profile')}
                                className={`${activeTab === 'profile'
                                    ? 'bg-gray-50 text-green-700 hover:text-green-700 hover:bg-white'
                                    : 'text-gray-900 hover:text-gray-900 hover:bg-gray-50'}
                                    group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full
                                    ${activeTab === 'profile' ? 'border-l-4 border-green-600' : ''}`}
                            >
                                <i className={`fas fa-user-circle flex-shrink-0 -ml-1 mr-3 h-6 w-6 
                                    ${activeTab === 'profile' ? 'text-green-500' : 'text-gray-400 group-hover:text-gray-500'}`}></i>
                                <span className="truncate">Profile</span>
                            </button>

                            <button
                                onClick={() => setActiveTab('branding')}
                                className={`${activeTab === 'branding'
                                    ? 'bg-gray-50 text-green-700 hover:text-green-700 hover:bg-white'
                                    : 'text-gray-900 hover:text-gray-900 hover:bg-gray-50'}
                                    group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full
                                    ${activeTab === 'branding' ? 'border-l-4 border-green-600' : ''}`}
                            >
                                <i className={`fas fa-paint-brush flex-shrink-0 -ml-1 mr-3 h-6 w-6
                                    ${activeTab === 'branding' ? 'text-green-500' : 'text-gray-400 group-hover:text-gray-500'}`}></i>
                                <span className="truncate">Email Branding</span>
                            </button>

                            <button
                                onClick={() => setActiveTab('subscription')}
                                className={`${activeTab === 'subscription'
                                    ? 'bg-gray-50 text-green-700 hover:text-green-700 hover:bg-white'
                                    : 'text-gray-900 hover:text-gray-900 hover:bg-gray-50'}
                                    group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full
                                    ${activeTab === 'subscription' ? 'border-l-4 border-green-600' : ''}`}
                            >
                                <i className={`fas fa-credit-card flex-shrink-0 -ml-1 mr-3 h-6 w-6 
                                    ${activeTab === 'subscription' ? 'text-green-500' : 'text-gray-400 group-hover:text-gray-500'}`}></i>
                                <span className="truncate">Subscription</span>
                            </button>

                            <button
                                onClick={() => setActiveTab('audit')}
                                className={`${activeTab === 'audit'
                                    ? 'bg-gray-50 text-green-700 hover:text-green-700 hover:bg-white'
                                    : 'text-gray-900 hover:text-gray-900 hover:bg-gray-50'}
                                    group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full
                                    ${activeTab === 'audit' ? 'border-l-4 border-green-600' : ''}`}
                            >
                                <i className={`fas fa-shield-alt flex-shrink-0 -ml-1 mr-3 h-6 w-6 
                                    ${activeTab === 'audit' ? 'text-green-500' : 'text-gray-400 group-hover:text-gray-500'}`}></i>
                                <span className="truncate">Activity Log</span>
                            </button>

                        </nav>
                    </aside>

                    {/* Main Content Area */}
                    <div className="space-y-6 sm:px-6 lg:px-0 lg:col-span-9">
                        {renderContent()}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
